import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage } from '@/lib/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  HOSPITAL_ADMIN: 'hospital_admin',
  DOCTOR: 'doctor',
  NURSE: 'nurse',
  RECEPTIONIST: 'receptionist',
  LAB_TECHNICIAN: 'lab_technician',
  PHARMACIST: 'pharmacist',
  ACCOUNTANT: 'accountant',
};

function persistSession(authPayload) {
  if (!authPayload?.accessToken || !authPayload?.user) return;
  localStorage.setItem('hms-access-token', authPayload.accessToken);
  localStorage.setItem('hms-user', JSON.stringify(authPayload.user));
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const applyAuthPayload = useCallback((authPayload) => {
    persistSession(authPayload);
    setUser(authPayload.user);
    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('hms-access-token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        setUser(data);
        setIsAuthenticated(true);
        localStorage.setItem('hms-user', JSON.stringify(data));
      } catch {
        localStorage.removeItem('hms-access-token');
        localStorage.removeItem('hms-user');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      applyAuthPayload(data);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error) };
    }
  };

  const register = async (companyData) => {
    try {
      const { data } = await api.post('/auth/register', {
        hospitalName: companyData.hospitalName,
        address: companyData.address,
        phone: companyData.phone,
        email: companyData.email,
        adminName: companyData.adminName,
        password: companyData.password,
      });
      applyAuthPayload(data);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error) };
    }
  };

  const registerHospital = async (formPayload) => {
    try {
      const { data } = await api.post('/auth/register-hospital', formPayload);
      applyAuthPayload(data);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: getApiErrorMessage(error) };
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('hms-user');
    localStorage.removeItem('hms-access-token');
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  /** Server-driven module access (hospital_admin / super_admin always pass). */
  const hasModule = (moduleId) => {
    if (!user || !moduleId) return false;
    if (user.role === ROLES.HOSPITAL_ADMIN || user.role === ROLES.SUPER_ADMIN) {
      return true;
    }
    const mods = user.allowedModules;
    if (!Array.isArray(mods)) {
      return true;
    }
    return mods.includes(moduleId);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      localStorage.setItem('hms-user', JSON.stringify(data));
    } catch {
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        register,
        registerHospital,
        logout,
        hasRole,
        hasModule,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
